import React, { useEffect, useState } from 'react';
import * as S from '../Advances/AdvancesStyles';
import PhotoLightbox from './PhotoLightbox';
import { getPollinationFarmerHub, getPollinationDeployments, getPollinationObservations } from '../../../../services/pollinationService';

const STATUS_VARIANT = {
  INTERESTED: 'default',
  CONSENTED: 'advance',
  BASELINE_DONE: 'pending',
  HIVES_DEPLOYED: 'approved',
  OBSERVING: 'approved',
  HARVEST_LOGGED: 'settled',
};
const STATUS_LABEL = {
  INTERESTED: 'Interested',
  CONSENTED: 'Consented',
  BASELINE_DONE: 'Baseline done',
  HIVES_DEPLOYED: 'Hives deployed',
  OBSERVING: 'Observing',
  HARVEST_LOGGED: 'Harvest logged',
};

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');


const EMPTY = <div style={{ color: '#9CA3AF', padding: '16px 4px' }}>Not recorded yet.</div>;

// Single-record stages (baseline, harvest): field / value rows.
const FieldTable = ({ rows }) => (
  <S.TableWrap>
    <table>
      <thead><tr><th style={{ width: '40%' }}>Field</th><th>Value</th></tr></thead>
      <tbody>
        {rows.map(([label, value]) => (
          <tr key={label}><td className="rowmuted">{label}</td><td>{value === undefined || value === null || value === '' ? '—' : value}</td></tr>
        ))}
      </tbody>
    </table>
  </S.TableWrap>
);

// Multi-record stages (deployments, observations): one row per entry.
const RecordTable = ({ columns, rows, emptyText }) => (
  rows.length === 0 ? (
    <div style={{ color: '#9CA3AF', padding: '16px 4px' }}>{emptyText}</div>
  ) : (
    <S.TableWrap>
      <table>
        <thead><tr>{columns.map((c) => <th key={c.label}>{c.label}</th>)}</tr></thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row._id}>{columns.map((c) => <td key={c.label}>{c.render(row)}</td>)}</tr>
          ))}
        </tbody>
      </table>
    </S.TableWrap>
  )
);

const DEPLOYMENT_COLUMNS = [
  { label: 'Placed', render: (d) => fmtDate(d.date_placed) },
  { label: 'Hives', render: (d) => d.hive_count ?? '—' },
  { label: 'Type', render: (d) => d.hive_type || '—' },
  { label: 'Removed', render: (d) => (d.date_removed ? fmtDate(d.date_removed) : 'Not yet removed') },
  { label: 'Removal reason', render: (d) => d.removal_reason || '—' },
];

const buildObservationColumns = (openPhoto) => [
  { label: 'Visit', render: (o) => fmtDate(o.visit_date) },
  { label: 'Healthy', render: (o) => o.healthy_hives ?? '—' },
  { label: 'Unhealthy', render: (o) => o.unhealthy_hives ?? '—' },
  { label: 'Spraying nearby', render: (o) => o.spraying_nearby || '—' },
  { label: 'Actions taken', render: (o) => (o.actions_taken || []).join(', ') || '—' },
  { label: 'Next visit', render: (o) => (o.next_visit_date ? fmtDate(o.next_visit_date) : '—') },
  {
    label: 'Photo',
    render: (o) => (o.photo_url
      ? (
        <img
          src={o.photo_url}
          alt="Observation"
          title="Click to enlarge"
          onClick={() => openPhoto(o.photo_url)}
          style={{ width: 48, height: 48, borderRadius: 6, objectFit: 'cover', cursor: 'zoom-in' }}
        />
      )
      : '—'),
  },
];

/**
 * Read-only — the office looks at what the field agent already recorded on
 * mobile, nothing here is editable. Baseline/harvest come from the same
 * bundled hub endpoint the mobile Farmer Hub screen uses; deployment and
 * observation sections pull the full history (not just the latest) from the
 * same list endpoints mobile's own history screens now use, so this never
 * drifts from what the field agent actually sees.
 */
const FarmerDetailDrawer = ({ show, farmerId, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [farmer, setFarmer] = useState(null);
  const [stages, setStages] = useState(null);
  const [deployments, setDeployments] = useState([]);
  const [observations, setObservations] = useState([]);
  const [activeTab, setActiveTab] = useState('baseline');
  const [photoUrl, setPhotoUrl] = useState(null);

  useEffect(() => {
    if (!show || !farmerId) return;
    let cancelled = false;
    setActiveTab('baseline');
    setPhotoUrl(null);
    (async () => {
      setLoading(true);
      try {
        const [hub, deploymentList, observationList] = await Promise.all([
          getPollinationFarmerHub(farmerId),
          getPollinationDeployments(farmerId),
          getPollinationObservations(farmerId),
        ]);
        if (!cancelled) {
          setFarmer(hub.farmer);
          setStages(hub.stages);
          setDeployments(deploymentList);
          setObservations(observationList);
        }
      } catch (err) {
        console.error('Failed to load farmer details:', err);
      }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [show, farmerId]);

  const b = stages?.baseline?.data || {};
  const h = stages?.harvest?.data || {};
  const baselineRows = [
    ['Area under crop', b.area_under_crop !== undefined ? `${b.area_under_crop} ${b.area_unit?.toLowerCase() || ''}${b.area_under_crop_hectares ? ` (${b.area_under_crop_hectares} ha)` : ''}` : ''],
    ['Total farm area', b.total_farm_area !== undefined ? `${b.total_farm_area} ${b.area_unit?.toLowerCase() || ''}` : ''],
    ['Crop variety', b.crop_variety],
    ['Seed type', b.seed_type === 'GMO' ? 'GMO' : 'Non-GMO'],
    ['Pesticides on farm', b.pesticides_used === 'PRODUCTS' ? (b.pesticide_products || []).join(', ') || 'Products' : 'None'],
    ['Nearby crops', (b.nearby_crops || []).join(', ')],
    ['Nearby pesticide use', b.nearby_pesticide_use],
    ['Hives planned', b.hives_planned],
    ['Previous year yield', b.previous_year_yield ? `${b.previous_year_yield} ${b.previous_year_yield_unit?.toLowerCase() || ''}` : ''],
  ];
  const harvestRows = [
    ['Yield', h.yield_value !== undefined ? `${h.yield_value} ${h.yield_unit?.toLowerCase() || ''}` : ''],
    ['Recorded', h.date_recorded ? fmtDate(h.date_recorded) : ''],
    ['Previous year yield', h.previous_year_yield],
  ];
  const tabs = [
    { key: 'baseline', label: 'Baseline', badge: stages?.baseline?.done ? '✓' : '' },
    { key: 'deployments', label: 'Hive deployments', badge: deployments.length },
    { key: 'observations', label: 'Observation logs', badge: observations.length },
    { key: 'harvest', label: 'Harvest log', badge: stages?.harvest?.done ? '✓' : '' },
  ];

  return (
    <>
      <S.DrawerOverlay show={show} onClick={onClose} />
      <S.DrawerContainer show={show} style={{ width: 'min(920px, 96vw)' }}>
        <div className="drawer-head">
          <button className="close" onClick={onClose}>✕</button>
          <div className="drawer-name">{farmer?.name || 'Farmer'}</div>
          <div className="drawer-role">
            {[farmer?.village, farmer?.block].filter(Boolean).join(', Block ') || 'Pollination farmer'}
          </div>
          {farmer && (
            <div style={{ marginTop: 10 }}>
              <S.Pill variant={STATUS_VARIANT[farmer.status]}>{STATUS_LABEL[farmer.status] || farmer.status}</S.Pill>
            </div>
          )}
        </div>

        <div className="drawer-body">
          {loading ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#6B7280' }}>Loading…</div>
          ) : !stages ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#6B7280' }}>Could not load this farmer.</div>
          ) : (
            <>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', margin: '12px 0 16px' }}>
                {tabs.map((t) => (
                  <S.Btn key={t.key} sm ghost={activeTab !== t.key} onClick={() => setActiveTab(t.key)}>
                    {t.label}{t.badge ? ` (${t.badge})` : ''}
                  </S.Btn>
                ))}
              </div>

              {activeTab === 'baseline' && (
                stages.baseline.done ? <FieldTable rows={baselineRows} /> : EMPTY
              )}
              {activeTab === 'deployments' && (
                <RecordTable columns={DEPLOYMENT_COLUMNS} rows={deployments} emptyText="Not recorded yet." />
              )}
              {activeTab === 'observations' && (
                <RecordTable columns={buildObservationColumns(setPhotoUrl)} rows={observations} emptyText="No visits logged yet." />
              )}
              {activeTab === 'harvest' && (
                stages.harvest.done ? <FieldTable rows={harvestRows} /> : EMPTY
              )}
            </>
          )}
        </div>
      </S.DrawerContainer>
      <PhotoLightbox url={photoUrl} onClose={() => setPhotoUrl(null)} alt="Observation" />
    </>
  );
};

export default FarmerDetailDrawer;
